"use client"

import * as React from 'react'
import Link from 'next/link'
import { z } from 'zod'
import { Plus } from 'lucide-react'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import type { CrudCustomFieldRenderProps, CrudField, CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { Button } from '@open-mercato/ui/primitives/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@open-mercato/ui/primitives/dialog'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import {
  formatParticipantTypeLabel,
  formatSupportedModeLabel,
  formatSupportedSegmentLabel,
  participantTypeValues,
  supportedModeValues,
  supportedSegmentValues,
} from '../lib/participantProfile'

export type ParticipantFormValues = {
  customerEntityId: string
  participantType: (typeof participantTypeValues)[number]
  supportedModes: (typeof supportedModeValues)[number][]
  supportedSegments: (typeof supportedSegmentValues)[number][]
  nip?: string
  regon?: string
  krs?: string
  vatEu?: string
  eori?: string
  fleetTrucks?: number
  fleetTrailers?: number
  fleetLocomotives?: number
  fleetWagons?: number
  fleetTotalPayloadTons?: number
  paymentTermsDays?: number
  creditLimit?: number
  creditLimitCurrency?: string
  currentExposure?: number
  marketplaceGrade?: string
  marketplaceScore?: number
  totalShipmentsCompleted?: number
  totalVolumeTons?: number
  activeContractCount?: number
  activeLaneCount?: number
  memberSince?: string
  isVerified?: boolean
  verifiedAt?: string
  verificationNotes?: string
  isActive?: boolean
} & Record<string, unknown>

type ParticipantDetailPayload = {
  participant: Record<string, unknown>
  linkedCustomer: {
    id: string
    displayName: string
    primaryEmail?: string | null
    primaryPhone?: string | null
    status?: string | null
    lifecycleStage?: string | null
  } | null
  customFields?: Record<string, unknown>
}

type CompanyOption = {
  value: string
  label: string
}

type CustomerCompanySelectLabels = {
  placeholder: string
  loadingLabel: string
  emptyLabel: string
  createLabel: string
  dialogTitle: string
  dialogDescription: string
  errorLoad: string
}

function normalizeCompanyOption(raw: unknown): CompanyOption | null {
  if (!raw || typeof raw !== 'object') return null
  const candidate = raw as Record<string, unknown>
  const id = typeof candidate.id === 'string' ? candidate.id : null
  if (!id) return null
  const label =
    typeof candidate.customerDisplayName === 'string' && candidate.customerDisplayName.trim().length
      ? candidate.customerDisplayName.trim()
      : typeof candidate.display_name === 'string' && candidate.display_name.trim().length
        ? candidate.display_name.trim()
        : typeof candidate.displayName === 'string' && candidate.displayName.trim().length
          ? candidate.displayName.trim()
          : null
  if (!label) return null
  return { value: id, label }
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableString(value: unknown): string | null {
  return normalizeString(value) ?? null
}

function normalizeStringArray<T extends string>(value: unknown): T[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is T => typeof entry === 'string' && entry.trim().length > 0)
}

function normalizeOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function normalizeNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function CustomerCompanySelectField({
  value,
  onChange,
  labels,
}: {
  value?: string
  onChange: (value: string | undefined) => void
  labels: CustomerCompanySelectLabels
}) {
  const [options, setOptions] = React.useState<CompanyOption[]>([])
  const [loading, setLoading] = React.useState(true)
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const loadOptions = React.useCallback(async () => {
    setLoading(true)
    try {
      const payload = await readApiResultOrThrow<{ items?: unknown[] }>(
        '/api/customers/companies?pageSize=100&sortField=name&sortDir=asc',
        undefined,
        { errorMessage: labels.errorLoad },
      )
      const baseOptions = (Array.isArray(payload?.items) ? payload.items : [])
        .map((entry) => normalizeCompanyOption(entry))
        .filter((entry): entry is CompanyOption => entry !== null)
      if (value && !baseOptions.some((entry) => entry.value === value)) {
        const current = await readApiResultOrThrow<{ items?: unknown[] }>(
          `/api/customers/companies?id=${encodeURIComponent(value)}&pageSize=1`,
          undefined,
          { errorMessage: labels.errorLoad },
        )
        const extraOptions = (Array.isArray(current?.items) ? current.items : [])
          .map((entry) => normalizeCompanyOption(entry))
          .filter((entry): entry is CompanyOption => entry !== null)
        setOptions(
          [...baseOptions, ...extraOptions].sort((left, right) =>
            left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
          ),
        )
        return
      }
      setOptions(
        baseOptions.sort((left, right) =>
          left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
        ),
      )
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : labels.errorLoad
      flash(message, 'error')
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [labels.errorLoad, value])

  React.useEffect(() => {
    void loadOptions()
  }, [loadOptions])

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select
          className="h-9 w-full rounded border px-3 text-sm"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value || undefined)}
          disabled={loading}
        >
          <option value="">{labels.placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="outline" size="icon" title={labels.createLabel} aria-label={labels.createLabel}>
              <Plus className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{labels.dialogTitle}</DialogTitle>
              <DialogDescription>{labels.dialogDescription}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end">
              <Button asChild variant="default" onClick={() => setDialogOpen(false)}>
                <Link href="/backend/customers/companies/create">{labels.createLabel}</Link>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-xs text-muted-foreground">{labels.loadingLabel}</div> : null}
      {!loading && options.length === 0 ? <div className="text-xs text-muted-foreground">{labels.emptyLabel}</div> : null}
    </div>
  )
}

export function createParticipantFormSchema() {
  return z.object({
    customerEntityId: z.string().uuid(),
    participantType: z.enum(participantTypeValues),
    supportedModes: z.array(z.enum(supportedModeValues)).default([]),
    supportedSegments: z.array(z.enum(supportedSegmentValues)).default([]),
    nip: z.string().trim().max(20).optional(),
    regon: z.string().trim().max(20).optional(),
    krs: z.string().trim().max(20).optional(),
    vatEu: z.string().trim().max(30).optional(),
    eori: z.string().trim().max(30).optional(),
    fleetTrucks: z.number().int().min(0).default(0),
    fleetTrailers: z.number().int().min(0).default(0),
    fleetLocomotives: z.number().int().min(0).default(0),
    fleetWagons: z.number().int().min(0).default(0),
    fleetTotalPayloadTons: z.number().min(0).default(0),
    paymentTermsDays: z.number().int().min(0).default(30),
    creditLimit: z.number().min(0).optional(),
    creditLimitCurrency: z.string().trim().max(3).optional(),
    currentExposure: z.number().min(0).default(0),
    marketplaceGrade: z.string().trim().max(2).optional(),
    marketplaceScore: z.number().min(0).max(100).optional(),
    totalShipmentsCompleted: z.number().int().min(0).default(0),
    totalVolumeTons: z.number().min(0).default(0),
    activeContractCount: z.number().int().min(0).default(0),
    activeLaneCount: z.number().int().min(0).default(0),
    memberSince: z.string().optional(),
    isVerified: z.boolean().default(false),
    verifiedAt: z.string().optional(),
    verificationNotes: z.string().trim().max(4000).optional(),
    isActive: z.boolean().default(true),
  })
}

export function createParticipantFormFields(
  t: (key: string, fallbackOrParams?: string | Record<string, string | number>, params?: Record<string, string | number>) => string,
): CrudField[] {
  const participantTypeOptions = participantTypeValues.map((value) => ({
    value,
    label: t(`participants.types.${value.toLowerCase()}`, formatParticipantTypeLabel(value)),
  }))
  const supportedModeOptions = supportedModeValues.map((value) => ({
    value,
    label: t(`participants.modes.${value.toLowerCase()}`, formatSupportedModeLabel(value)),
  }))
  const supportedSegmentOptions = supportedSegmentValues.map((value) => ({
    value,
    label: t(`participants.segments.${value.toLowerCase()}`, formatSupportedSegmentLabel(value)),
  }))

  return [
    {
      id: 'customerEntityId',
      label: t('participants.form.customerEntityId', 'CRM company'),
      type: 'custom',
      layout: 'half',
      component: ({ value, setValue }: CrudCustomFieldRenderProps) => (
        <CustomerCompanySelectField
          value={typeof value === 'string' ? value : undefined}
          onChange={(next) => setValue(next)}
          labels={{
            placeholder: t('participants.form.customer.placeholder', 'Select a company'),
            loadingLabel: t('participants.form.customer.loading', 'Loading companies…'),
            emptyLabel: t('participants.form.customer.empty', 'No CRM companies available yet.'),
            createLabel: t('participants.form.customer.create', 'Create company'),
            dialogTitle: t('participants.form.customer.dialogTitle', 'Create CRM company first'),
            dialogDescription: t(
              'participants.form.customer.dialogDescription',
              'Participant profiles extend existing customer companies, so we first need a CRM company record.',
            ),
            errorLoad: t('participants.form.customer.errorLoad', 'Failed to load companies.'),
          }}
        />
      ),
    },
    {
      id: 'participantType',
      label: t('participants.form.participantType', 'Participant type'),
      type: 'select',
      required: true,
      layout: 'half',
      options: participantTypeOptions,
    },
    {
      id: 'supportedModes',
      label: t('participants.form.supportedModes', 'Supported modes'),
      type: 'select',
      layout: 'full',
      multiple: true,
      listbox: true,
      options: supportedModeOptions,
    },
    {
      id: 'supportedSegments',
      label: t('participants.form.supportedSegments', 'Supported segments'),
      type: 'select',
      layout: 'full',
      multiple: true,
      listbox: true,
      options: supportedSegmentOptions,
    },
    { id: 'nip', label: t('participants.form.nip', 'NIP'), type: 'text', layout: 'third' },
    { id: 'regon', label: t('participants.form.regon', 'REGON'), type: 'text', layout: 'third' },
    { id: 'krs', label: t('participants.form.krs', 'KRS'), type: 'text', layout: 'third' },
    { id: 'vatEu', label: t('participants.form.vatEu', 'VAT-EU'), type: 'text', layout: 'half' },
    { id: 'eori', label: t('participants.form.eori', 'EORI'), type: 'text', layout: 'half' },
    { id: 'fleetTrucks', label: t('participants.form.fleetTrucks', 'Trucks'), type: 'number', layout: 'third' },
    { id: 'fleetTrailers', label: t('participants.form.fleetTrailers', 'Trailers'), type: 'number', layout: 'third' },
    { id: 'fleetLocomotives', label: t('participants.form.fleetLocomotives', 'Locomotives'), type: 'number', layout: 'third' },
    { id: 'fleetWagons', label: t('participants.form.fleetWagons', 'Wagons'), type: 'number', layout: 'third' },
    {
      id: 'fleetTotalPayloadTons',
      label: t('participants.form.fleetTotalPayloadTons', 'Fleet payload (t)'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'paymentTermsDays',
      label: t('participants.form.paymentTermsDays', 'Payment terms (days)'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'creditLimit',
      label: t('participants.form.creditLimit', 'Credit limit'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'creditLimitCurrency',
      label: t('participants.form.creditLimitCurrency', 'Credit limit currency'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'currentExposure',
      label: t('participants.form.currentExposure', 'Current exposure'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'marketplaceGrade',
      label: t('participants.form.marketplaceGrade', 'Marketplace grade'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'marketplaceScore',
      label: t('participants.form.marketplaceScore', 'Marketplace score'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'totalShipmentsCompleted',
      label: t('participants.form.totalShipmentsCompleted', 'Completed shipments'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'totalVolumeTons',
      label: t('participants.form.totalVolumeTons', 'Volume handled (t)'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'activeContractCount',
      label: t('participants.form.activeContractCount', 'Active contracts'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'activeLaneCount',
      label: t('participants.form.activeLaneCount', 'Active lanes'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'memberSince',
      label: t('participants.form.memberSince', 'Member since'),
      type: 'datepicker',
      layout: 'half',
    },
    {
      id: 'verifiedAt',
      label: t('participants.form.verifiedAt', 'Verified at'),
      type: 'datepicker',
      layout: 'half',
    },
    {
      id: 'isVerified',
      label: t('participants.form.isVerified', 'Verified participant'),
      type: 'checkbox',
      layout: 'half',
    },
    {
      id: 'isActive',
      label: t('participants.form.isActive', 'Active participant'),
      type: 'checkbox',
      layout: 'half',
    },
    {
      id: 'verificationNotes',
      label: t('participants.form.verificationNotes', 'Verification notes'),
      type: 'textarea',
      layout: 'full',
    },
  ]
}

export function createParticipantFormGroups(
  t: (key: string, fallbackOrParams?: string | Record<string, string | number>, params?: Record<string, string | number>) => string,
): CrudFormGroup[] {
  return [
    {
      id: 'participant_identity',
      title: t('participants.form.groups.identity.title', 'Participant identity'),
      description: t(
        'participants.form.groups.identity.description',
        'Link the freight profile to an existing CRM company and define its operating role.',
      ),
      column: 2,
      fields: ['customerEntityId', 'participantType', 'supportedModes', 'supportedSegments'],
    },
    {
      id: 'participant_identifiers',
      title: t('participants.form.groups.identifiers.title', 'Business identifiers'),
      description: t(
        'participants.form.groups.identifiers.description',
        'Store the core fiscal and customs identifiers used in freight onboarding.',
      ),
      column: 2,
      fields: ['nip', 'regon', 'krs', 'vatEu', 'eori'],
    },
    {
      id: 'participant_operations',
      title: t('participants.form.groups.operations.title', 'Operational summary'),
      description: t(
        'participants.form.groups.operations.description',
        'Capture the denormalized fleet and lane capacity snapshot surfaced to operators.',
      ),
      column: 2,
      fields: ['fleetTrucks', 'fleetTrailers', 'fleetLocomotives', 'fleetWagons', 'fleetTotalPayloadTons'],
    },
    {
      id: 'participant_commercial',
      title: t('participants.form.groups.commercial.title', 'Commercial and marketplace summary'),
      description: t(
        'participants.form.groups.commercial.description',
        'Track financial exposure, scorecard signals, and denormalized activity counters.',
      ),
      column: 2,
      fields: [
        'paymentTermsDays',
        'creditLimit',
        'creditLimitCurrency',
        'currentExposure',
        'marketplaceGrade',
        'marketplaceScore',
        'totalShipmentsCompleted',
        'totalVolumeTons',
        'activeContractCount',
        'activeLaneCount',
        'memberSince',
      ],
    },
    {
      id: 'participant_status',
      title: t('participants.form.groups.status.title', 'Verification and status'),
      description: t(
        'participants.form.groups.status.description',
        'Mark whether the participant is verified and active in freight operations.',
      ),
      column: 2,
      fields: ['isVerified', 'isActive', 'verifiedAt', 'verificationNotes'],
    },
    {
      id: 'participant_custom_fields',
      title: t('participants.form.groups.customFields.title', 'Custom fields'),
      kind: 'customFields',
    },
  ]
}

export function buildParticipantCreatePayload(values: ParticipantFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    customerEntityId: values.customerEntityId,
    participantType: values.participantType,
    supportedModes: normalizeStringArray(values.supportedModes),
    supportedSegments: normalizeStringArray(values.supportedSegments),
    fleetTrucks: normalizeOptionalNumber(values.fleetTrucks) ?? 0,
    fleetTrailers: normalizeOptionalNumber(values.fleetTrailers) ?? 0,
    fleetLocomotives: normalizeOptionalNumber(values.fleetLocomotives) ?? 0,
    fleetWagons: normalizeOptionalNumber(values.fleetWagons) ?? 0,
    fleetTotalPayloadTons: normalizeOptionalNumber(values.fleetTotalPayloadTons) ?? 0,
    paymentTermsDays: normalizeOptionalNumber(values.paymentTermsDays) ?? 30,
    currentExposure: normalizeOptionalNumber(values.currentExposure) ?? 0,
    totalShipmentsCompleted: normalizeOptionalNumber(values.totalShipmentsCompleted) ?? 0,
    totalVolumeTons: normalizeOptionalNumber(values.totalVolumeTons) ?? 0,
    activeContractCount: normalizeOptionalNumber(values.activeContractCount) ?? 0,
    activeLaneCount: normalizeOptionalNumber(values.activeLaneCount) ?? 0,
    isVerified: values.isVerified === true,
    isActive: values.isActive !== false,
  }

  const nullableCreateFields: Array<[string, unknown]> = [
    ['nip', normalizeString(values.nip)],
    ['regon', normalizeString(values.regon)],
    ['krs', normalizeString(values.krs)],
    ['vatEu', normalizeString(values.vatEu)],
    ['eori', normalizeString(values.eori)],
    ['creditLimit', normalizeOptionalNumber(values.creditLimit)],
    ['creditLimitCurrency', normalizeString(values.creditLimitCurrency)],
    ['marketplaceGrade', normalizeString(values.marketplaceGrade)],
    ['marketplaceScore', normalizeOptionalNumber(values.marketplaceScore)],
    ['memberSince', normalizeString(values.memberSince)],
    ['verifiedAt', normalizeString(values.verifiedAt)],
    ['verificationNotes', normalizeString(values.verificationNotes)],
  ]

  nullableCreateFields.forEach(([key, value]) => {
    if (value !== undefined) payload[key] = value
  })

  return payload
}

export function buildParticipantUpdatePayload(id: string, values: ParticipantFormValues): Record<string, unknown> {
  return {
    id,
    customerEntityId: values.customerEntityId,
    participantType: values.participantType,
    supportedModes: normalizeStringArray(values.supportedModes),
    supportedSegments: normalizeStringArray(values.supportedSegments),
    nip: normalizeNullableString(values.nip),
    regon: normalizeNullableString(values.regon),
    krs: normalizeNullableString(values.krs),
    vatEu: normalizeNullableString(values.vatEu),
    eori: normalizeNullableString(values.eori),
    fleetTrucks: normalizeOptionalNumber(values.fleetTrucks) ?? 0,
    fleetTrailers: normalizeOptionalNumber(values.fleetTrailers) ?? 0,
    fleetLocomotives: normalizeOptionalNumber(values.fleetLocomotives) ?? 0,
    fleetWagons: normalizeOptionalNumber(values.fleetWagons) ?? 0,
    fleetTotalPayloadTons: normalizeOptionalNumber(values.fleetTotalPayloadTons) ?? 0,
    paymentTermsDays: normalizeOptionalNumber(values.paymentTermsDays) ?? 30,
    creditLimit: normalizeNullableNumber(values.creditLimit),
    creditLimitCurrency: normalizeString(values.creditLimitCurrency) ?? 'PLN',
    currentExposure: normalizeOptionalNumber(values.currentExposure) ?? 0,
    marketplaceGrade: normalizeNullableString(values.marketplaceGrade),
    marketplaceScore: normalizeNullableNumber(values.marketplaceScore),
    totalShipmentsCompleted: normalizeOptionalNumber(values.totalShipmentsCompleted) ?? 0,
    totalVolumeTons: normalizeOptionalNumber(values.totalVolumeTons) ?? 0,
    activeContractCount: normalizeOptionalNumber(values.activeContractCount) ?? 0,
    activeLaneCount: normalizeOptionalNumber(values.activeLaneCount) ?? 0,
    memberSince: normalizeString(values.memberSince) ?? null,
    isVerified: values.isVerified === true,
    verifiedAt: normalizeString(values.verifiedAt) ?? null,
    verificationNotes: normalizeNullableString(values.verificationNotes),
    isActive: values.isActive !== false,
  }
}

export function createParticipantInitialValues(customerEntityId?: string | null): ParticipantFormValues {
  return {
    customerEntityId: customerEntityId ?? '',
    participantType: 'SHIPPER',
    supportedModes: [],
    supportedSegments: [],
    fleetTrucks: 0,
    fleetTrailers: 0,
    fleetLocomotives: 0,
    fleetWagons: 0,
    fleetTotalPayloadTons: 0,
    paymentTermsDays: 30,
    currentExposure: 0,
    totalShipmentsCompleted: 0,
    totalVolumeTons: 0,
    activeContractCount: 0,
    activeLaneCount: 0,
    creditLimitCurrency: 'PLN',
    isVerified: false,
    isActive: true,
  }
}

export function mapParticipantDetailToFormValues(detail: ParticipantDetailPayload): ParticipantFormValues {
  const participant = detail.participant ?? {}
  const customFields = detail.customFields ?? {}
  return {
    customerEntityId: normalizeString(participant.customerEntityId) ?? '',
    participantType: (
      normalizeString(participant.participantType) as ParticipantFormValues['participantType'] | undefined
    ) ?? 'SHIPPER',
    supportedModes: normalizeStringArray<(typeof supportedModeValues)[number]>(participant.supportedModes),
    supportedSegments: normalizeStringArray<(typeof supportedSegmentValues)[number]>(participant.supportedSegments),
    nip: normalizeString(participant.nip),
    regon: normalizeString(participant.regon),
    krs: normalizeString(participant.krs),
    vatEu: normalizeString(participant.vatEu),
    eori: normalizeString(participant.eori),
    fleetTrucks: normalizeOptionalNumber(participant.fleetTrucks) ?? 0,
    fleetTrailers: normalizeOptionalNumber(participant.fleetTrailers) ?? 0,
    fleetLocomotives: normalizeOptionalNumber(participant.fleetLocomotives) ?? 0,
    fleetWagons: normalizeOptionalNumber(participant.fleetWagons) ?? 0,
    fleetTotalPayloadTons: normalizeOptionalNumber(participant.fleetTotalPayloadTons) ?? 0,
    paymentTermsDays: normalizeOptionalNumber(participant.paymentTermsDays) ?? 30,
    creditLimit: normalizeOptionalNumber(participant.creditLimit),
    creditLimitCurrency: normalizeString(participant.creditLimitCurrency) ?? 'PLN',
    currentExposure: normalizeOptionalNumber(participant.currentExposure) ?? 0,
    marketplaceGrade: normalizeString(participant.marketplaceGrade),
    marketplaceScore: normalizeOptionalNumber(participant.marketplaceScore),
    totalShipmentsCompleted: normalizeOptionalNumber(participant.totalShipmentsCompleted) ?? 0,
    totalVolumeTons: normalizeOptionalNumber(participant.totalVolumeTons) ?? 0,
    activeContractCount: normalizeOptionalNumber(participant.activeContractCount) ?? 0,
    activeLaneCount: normalizeOptionalNumber(participant.activeLaneCount) ?? 0,
    memberSince: normalizeString(participant.memberSince),
    isVerified: participant.isVerified === true,
    verifiedAt: normalizeString(participant.verifiedAt),
    verificationNotes: normalizeString(participant.verificationNotes),
    isActive: participant.isActive !== false,
    ...customFields,
  }
}
